/**
 * WHAT THE GEOMETRY SAYS ABOUT ONE SHOOTER AND EVERYONE THEY CAN SEE.
 *
 * Cover, range band and hull facing are not properties of a defender - they
 * are facts about a PAIR. `measureBetween` has always known that; the only
 * thing that was single-target was the hook wrapped around it, which asked
 * about one `targetId` and returned one answer.
 *
 * That arity is why the app has a strange asymmetry today: ground surfaces
 * reach every combatant, but cover and facing reach only the one you happen
 * to be aiming at (`DeathWatchSimulator.jsx` - `t.id === activeTab`). Fine
 * while an attack could only touch one person. An area weapon needs the same
 * per-pair truth for everyone it catches.
 *
 * So this answers for a LIST of defenders in one call, and the hook becomes a
 * thin memo over it. Nothing here is new maths - it is the same measurement,
 * the same `measuredValues`, and the same `arcFor` bearing, run N times.
 *
 * PURE, and it writes nothing. Two clients deriving different cover for the
 * same defender is the normal case rather than a race, which is exactly why
 * none of it is ever stored.
 */
import { reachGap } from './reach';
import { measureBetween } from './measure';
import { hordeRadius, tokenEye, tokenHeight } from './tokens';
import { measuredValues } from './derived';
import { bearingBetween, arcFor } from './facing';
import type { Token } from './tokens';
import type { ArcPreset } from './facing';
import type { Solid } from './types';

/** What one shooter→defender pair works out to. */
export interface Relation {
  cover: ReturnType<typeof measuredValues>['cover'];
  rangeBand: ReturnType<typeof measuredValues>['rangeBand'];
  measurement: ReturnType<typeof measureBetween>;
  /** True when terrain stops the shooter seeing them at all. */
  blocked: boolean;
  /** Which arc the shot arrives on, or null when the map cannot say. VEHICLES only - it is what armour they present. */
  vehicleFacing: string | null;
  /** The same bearing for ANY defender with a heading (v8.368.2) - for a `rear` exposure; null without one. */
  arc: string | null;
  /** The gap a melee strike must cross, footprint to footprint (`reach.ts`), in metres. */
  reachGap: number;
}

/**
 * The table's rulings a relation reads, as ONE record built once in the shell (2026-10-26): how near the target cover
 * must stand (`coverNearTarget`, metres; null for "anywhere on the line"), and each horde's magnitude - a horde's
 * reach counts from the mob's edge. One record, so a new ruling is one field here, not a thread through every hop.
 */
export interface RelationRules {
  coverReach?: number | null;
  /** Magnitude per horde id (`hordeMagnitudes`). */
  hordeMagnitude?: Readonly<Record<string, number>>;
}

/**
 * Refuse the retired option names: `coverReach` and `hordeMagnitude` live inside `rules` now. From JavaScript nothing
 * type-checks them, and passed at the top level they would be dropped - the ruling lost without a word.
 */
export function refuseRetiredRules(options: object): void {
  if ('coverReach' in options || 'hordeMagnitude' in options) {
    throw new Error('coverReach and hordeMagnitude go inside `rules` (a RelationRules record)');
  }
}

export interface RelateOptions {
  weaponRange?: number;
  /** GM clients measure through terrain players are not shown. */
  includeHidden?: boolean;
  /** Ids whose hull facing should be resolved; others get null. */
  vehicleIds?: Set<string> | string[];
  arcPreset?: ArcPreset;
  rules?: RelationRules;
}

const EMPTY_SOLIDS: Solid[] = [];

/** Ids as a Set, whichever form they came in; null for none. */
function asSet(ids: RelateOptions['vehicleIds']): Set<string> | null {
  if (!ids) return null;
  return ids instanceof Set ? ids : new Set(ids);
}

/**
 * Which arc of the defender the shot arrives in, or null when they face
 * nowhere. The bearing runs FROM THE DEFENDER TO THE SHOOTER - which side
 * the shot arrives on, not which way it travels. Backwards swaps front
 * and rear and looks entirely plausible.
 */
function arcToShooter(shooter: Token, defender: Token, arcPreset: ArcPreset) {
  return defender.heading !== undefined ? arcFor(defender.heading, bearingBetween(defender, shooter), arcPreset) : null;
}

/**
 * Relate one shooter to many defenders.
 *
 * Skips the shooter themselves - nobody shoots their own token, and a
 * zero-length measurement produces a nonsense range band.
 */
export function relateFrom(
  shooter: Token | null | undefined,
  defenders: readonly Token[],
  solids: Solid[],
  options: RelateOptions = {},
): Map<string, Relation> {
  refuseRetiredRules(options);
  const out = new Map<string, Relation>();
  if (!shooter) return out;

  const { weaponRange, includeHidden = false, arcPreset = 'quarters', rules } = options;
  const one = {
    weaponRange,
    includeHidden,
    arcPreset,
    vehicles: asSet(options.vehicleIds),
    rules,
  };

  for (const defender of defenders) {
    if (!defender || defender.id === shooter.id) continue;
    out.set(defender.id, relateOne(shooter, defender, solids, one));
  }
  return out;
}

/** How one pair is measured: the weapon's reach, hidden terrain, the arc set, who counts as a vehicle. */
interface RelateOneOptions {
  weaponRange?: number;
  includeHidden?: boolean;
  arcPreset?: ArcPreset;
  vehicles?: Set<string> | null;
  isVehicle?: boolean;
  rules?: RelationRules;
}

/**
 * What a measurement needs of its TARGET: its height and feet (how much of it a wall hides) and, for the cover reach
 * (2026-10-25), its footprint - a radius - since cover counts only near its edge; for a horde, the mob's spread
 * (`hordeRadius` of its `rules.hordeMagnitude`, ruled 2026-10-26), so a barricade at its front ranks covers it. One
 * builder, so the live answer and the tape (useBattlefieldMeasure) cannot drift apart.
 */
export function targetEnd(target: Token, rules: RelationRules | undefined) {
  const magnitude = rules?.hordeMagnitude?.[target.id] ?? 0;
  return {
    targetHeight: tokenHeight(target),
    targetFeetY: target.elevation,
    // Absent is "anywhere on the line", the same as null.
    coverReach: rules?.coverReach ?? null,
    // A horde of one is one body (hordeRadius(1) is the footprint); never narrower than the marker itself.
    targetRadius: Math.max(target.footprint, hordeRadius(magnitude, target.footprint)),
  };
}

/** The single pair, exposed because the live-modifier hook still wants one. */
export function relateOne(shooter: Token, defender: Token, solids: Solid[], options: RelateOneOptions = {}): Relation {
  refuseRetiredRules(options);
  const { weaponRange, includeHidden = false, arcPreset = 'quarters', vehicles, isVehicle, rules } = options;

  // A token that ignores terrain measures through it, at both ends: a spirit
  // is neither given cover by a wall nor denied sight through one.
  const ignores = shooter.ignoresTerrain || defender.ignoresTerrain;

  // The TARGET end decides the cover - it is their body a shot has to reach,
  // and their height that says how much of them is hidden.
  const measurement = measureBetween(tokenEye(shooter), tokenEye(defender), ignores ? EMPTY_SOLIDS : solids, {
    weaponRange,
    includeHidden,
    ...targetEnd(defender, rules),
  });

  const { cover, rangeBand } = measuredValues(measurement);
  const arc = arcToShooter(shooter, defender, arcPreset);
  // WHICH ARMOUR THE HULL PRESENTS TO THIS SHOOTER: the same arc, read as
  // a facing only for a vehicle.
  const treatAsVehicle = isVehicle ?? (vehicles ? vehicles.has(defender.id) : false);
  const vehicleFacing = treatAsVehicle ? arc : null;

  return {
    cover,
    rangeBand,
    measurement,
    blocked: !!measurement.blocked,
    vehicleFacing,
    arc,
    reachGap: reachGap(shooter, defender),
  };
}
